import { HEADER_API_KEY, RETRY_BASE_DELAY, RETRY_429_DELAY } from './constants'
import type { Logger } from './logger'
import type { IngestRequest, IngestResponse, QueuedEvent } from './types'

export interface TransportConfig {
  endpoint: string
  apiKey?: string
  maxRetries: number
  logger: Logger
  onError?: (error: Error, events: QueuedEvent[]) => void
}

export class HttpTransport {
  private config: TransportConfig

  constructor(config: TransportConfig) {
    this.config = config
  }

  async send(payload: IngestRequest): Promise<IngestResponse | null> {
    const url = this.config.endpoint

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (this.config.apiKey) {
      headers[HEADER_API_KEY] = this.config.apiKey
    }

    const body = JSON.stringify(payload)

    for (let attempt = 0; attempt <= this.config.maxRetries; attempt++) {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body,
          keepalive: true,
        })

        // 4xx non-429: don't retry
        if (response.status >= 400 && response.status < 500 && response.status !== 429) {
          const errorMsg = `HTTP ${response.status}`
          this.config.logger.error('Non-retryable error:', errorMsg)
          this.config.onError?.(new Error(errorMsg), payload.events)
          return null
        }

        // 429: respect Retry-After
        if (response.status === 429) {
          if (attempt >= this.config.maxRetries) {
            break
          }
          const retryAfter = response.headers.get('Retry-After')
          const delay = retryAfter ? parseInt(retryAfter, 10) * 1000 : RETRY_429_DELAY
          this.config.logger.debug(`Rate limited, retrying in ${delay}ms`)
          await this.sleep(delay)
          continue
        }

        // 5xx: retry with backoff
        if (response.status >= 500) {
          if (attempt >= this.config.maxRetries) {
            break
          }
          const delay = RETRY_BASE_DELAY * Math.pow(2, attempt)
          this.config.logger.debug(`Server error ${response.status}, retrying in ${delay}ms`)
          await this.sleep(delay)
          continue
        }

        // 2xx success
        return (await response.json()) as IngestResponse
      } catch (error) {
        // Network error: retry with backoff
        if (attempt >= this.config.maxRetries) {
          this.config.logger.error('All retries exhausted', error)
          this.config.onError?.(error instanceof Error ? error : new Error(String(error)), payload.events)
          return null
        }
        const delay = RETRY_BASE_DELAY * Math.pow(2, attempt)
        this.config.logger.debug(`Network error, retrying in ${delay}ms`)
        await this.sleep(delay)
      }
    }

    // All retries exhausted
    this.config.onError?.(new Error('All retries exhausted'), payload.events)
    return null
  }

  sendBeacon(payload: IngestRequest): boolean {
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) {
      return false
    }
    const body = JSON.stringify(payload)
    return navigator.sendBeacon(this.config.endpoint, new Blob([body], { type: 'application/json' }))
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
}
